// VendAgain — listing detail. Shows photos gallery, price, description,
// seller info, and a "Contact seller" button that reveals their email
// via POST /vendagain/listings/{id}/contact (also emails the seller).
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Mail, ExternalLink, Trash2, Edit3, Check, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CategoryChip, ConditionPill, formatPrice, CAT_MAP } from "./shared";

export default function VendAgainDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/vendagain/listings/${id}`).then(({ data }) => {
      if (alive) setListing(data);
    }).catch(() => {
      toast.error("Listing not found");
      navigate("/vendagain");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, navigate]);

  const contactSeller = async () => {
    if (!user) { navigate("/login?next=" + encodeURIComponent(window.location.pathname)); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/vendagain/listings/${id}/contact`);
      setContact(data);
      toast.success(`We told ${data.owner_name} you're interested.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to contact seller");
    } finally { setBusy(false); }
  };

  const toggleSold = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/vendagain/listings/${id}/sold`, { sold: !listing.sold });
      setListing(data);
      toast.success(data.sold ? "Marked as sold." : "Relisted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally { setBusy(false); }
  };

  const deleteListing = async () => {
    if (!window.confirm("Delete this listing? This can't be undone.")) return;
    try {
      await api.delete(`/vendagain/listings/${id}`);
      toast.success("Listing deleted.");
      navigate("/vendagain/mine");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  if (loading) return <div className="mx-auto max-w-4xl px-6 py-14 opacity-60">Loading…</div>;
  if (!listing) return null;

  const cat = CAT_MAP[listing.category];
  const CatIcon = cat?.icon;

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="vendagain-detail-page">
      <Link to="/vendagain" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft size={14} /> Back to VendAgain
      </Link>

      <div className="mt-6 grid md:grid-cols-2 gap-10">
        {/* Photo gallery */}
        <div>
          <div
            className="aspect-square rounded-sm overflow-hidden bg-[color:var(--sand)] relative"
            data-testid="vendagain-detail-photo"
          >
            {listing.image_urls && listing.image_urls[activeImg] ? (
              <img
                src={listing.image_urls[activeImg]}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {CatIcon && <CatIcon className="h-20 w-20 opacity-20" style={{ color: cat.ink }} />}
              </div>
            )}
            {listing.sold && (
              <div
                className="absolute top-4 left-4 text-xs uppercase tracking-widest font-bold px-3 py-1 rounded-full"
                style={{ background: "rgba(0,0,0,0.8)", color: "#fff" }}
              >
                Sold
              </div>
            )}
          </div>
          {listing.image_urls && listing.image_urls.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2">
              {listing.image_urls.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  data-testid={`vendagain-detail-thumb-${i}`}
                  className={`aspect-square rounded-sm overflow-hidden ${i === activeImg ? "ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryChip value={listing.category} />
            <ConditionPill value={listing.condition} />
          </div>
          <h1 className="font-serif text-4xl mt-3 leading-tight" data-testid="vendagain-detail-title">
            {listing.title}
          </h1>
          <div className="mt-3 text-3xl font-bold" data-testid="vendagain-detail-price">
            {formatPrice(listing.price_cents, listing.best_offer)}
          </div>
          <div className="mt-2 text-sm opacity-70 inline-flex items-center gap-1">
            <MapPin size={12} /> {listing.city}
          </div>
          <p className="mt-6 whitespace-pre-wrap leading-relaxed" data-testid="vendagain-detail-desc">
            {listing.description}
          </p>

          {/* Owner card + actions */}
          <div className="mt-8 pt-6 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-60">Seller</div>
                <div className="font-bold">
                  {listing.owner.slug ? (
                    <Link to={`/vendors/${listing.owner.slug}`} className="underline decoration-dotted underline-offset-4">
                      {listing.owner.name}
                    </Link>
                  ) : listing.owner.name}
                </div>
              </div>
              {typeof listing.contact_requests === "number" && listing.is_owner && (
                <span className="text-xs opacity-60" data-testid="vendagain-contact-count">
                  {listing.contact_requests} buyer{listing.contact_requests === 1 ? "" : "s"} interested
                </span>
              )}
            </div>

            {listing.is_owner ? (
              <div className="flex flex-wrap gap-3">
                <Link to={`/vendagain/listings/${id}/edit`}>
                  <Button variant="outline" className="rounded-full inline-flex items-center gap-2" data-testid="vendagain-edit-btn">
                    <Edit3 size={14} /> Edit
                  </Button>
                </Link>
                <Button
                  onClick={toggleSold}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full inline-flex items-center gap-2"
                  data-testid="vendagain-sold-btn"
                >
                  {listing.sold ? <><RotateCcw size={14} /> Relist</> : <><Check size={14} /> Mark sold</>}
                </Button>
                <Button
                  onClick={deleteListing}
                  variant="outline"
                  className="rounded-full inline-flex items-center gap-2"
                  data-testid="vendagain-delete-btn"
                  style={{ color: "var(--terracotta)" }}
                >
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
            ) : contact ? (
              <div
                className="rounded-sm p-5"
                data-testid="vendagain-contact-revealed"
                style={{
                  background: "rgba(111,125,85,0.08)",
                  border: "1px solid rgba(111,125,85,0.3)",
                }}
              >
                <div className="text-sm opacity-70 mb-2">Reach out directly:</div>
                <a
                  href={`mailto:${contact.owner_email}?subject=Interested in your VendAgain listing: ${encodeURIComponent(listing.title)}`}
                  className="inline-flex items-center gap-2 font-bold underline decoration-dotted underline-offset-4"
                  style={{ color: "var(--moss)" }}
                  data-testid="vendagain-owner-email-link"
                >
                  <Mail size={14} /> {contact.owner_email}
                  <ExternalLink size={12} />
                </a>
                <div className="text-xs opacity-60 mt-2">
                  We&rsquo;ve also sent {contact.owner_name} an email letting them know.
                </div>
              </div>
            ) : listing.sold ? (
              <div className="text-sm opacity-70">This item has already been sold.</div>
            ) : (
              <Button
                onClick={contactSeller}
                disabled={busy}
                className="btn-primary rounded-full inline-flex items-center gap-2"
                data-testid="vendagain-contact-btn"
              >
                <Mail size={14} /> Contact seller
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
